/**
 * Whether the tutor has been redeployed under an open tab. The worker caches
 * no part of the app (installable design §4), so a reload already fetches the
 * new bundle; what a tab left open, or an installed app resumed, lacks is any
 * sign that there is one. The build ids come from tools/stamp_index.mjs.
 */

/** Rapid tab switches must not each cost a request. */
export const MIN_CHECK_GAP_MS = 60_000;

/** An app left open and visible for hours still learns of a deploy. */
export const CHECK_INTERVAL_MS = 60 * 60 * 1000;

export function pageBuild(doc: {
  querySelector(selector: string): { getAttribute(name: string): string | null } | null;
}) {
  return doc.querySelector('meta[name="tutor-build"]')?.getAttribute('content') || null;
}

/**
 * A missing version.json is answered with index.html by the client fallback,
 * so a body that is not the expected shape is no answer, never a new version.
 */
export function servedBuild(body: unknown): string | null {
  if (typeof body !== 'object' || body === null) return null;
  const build = (body as { build?: unknown }).build;
  return typeof build === 'string' && build !== '' ? build : null;
}

/** A resumed app makes no navigation, so it asks for itself. */
export function shouldCheck(visibility: DocumentVisibilityState, lastCheckMs: number, nowMs: number): boolean {
  return visibility === 'visible' && nowMs - lastCheckMs >= MIN_CHECK_GAP_MS;
}

/**
 * Held back while `busy`: a reload in the middle of a conversation, or with a
 * microphone open, ends the turn the learner is giving.
 */
export function noticeShown(state: {
  running: string | null;
  served: string | null;
  dismissed: boolean;
  busy: boolean;
}): boolean {
  const { running, served, dismissed, busy } = state;
  return running !== null && served !== null && served !== running && !dismissed && !busy;
}
